import { useState, useEffect, useCallback } from 'react'
import {
  type Proposal,
  type Package,
  type PackageBenefit,
  type ContractTemplate,
  type ProposalDetails,
  type CancellationPolicy,
  type BillingMode,
  type ProposalWaitlistResponse,
  type WaitlistStatus,
  type BookingLockMode,
  type ServiceCadenceUnit,
  type ServiceScheduleInput,
  type AvailabilityWindow,
  type AvailabilityException,
  type PublicBookingPageData,
  type TenantPublicProfile,
  type TenantPublicPageCustomization,
  type PublicPageThemeSelection,
  type BookingRequest,
} from '@/lib/types'

// ========================
// Tenant Settings Types
// ========================

export interface TenantSettings {
  id: string
  name: string
  slug: string
  planId: string
  currency: string
  isFounder: boolean
  founderSince?: string | null
  timeZone: string
  depositPercentage: number
  softHoldDurationHours: number
  cancellationWindowDays: number
  rescheduleFeePercentage: number
  defaultCancellationPolicyId: string | null
  lastCurrencyChangeDate?: string | null
  logoImageUrl?: string | null
  logoImagePublicId?: string | null
  noShowFeeEnabled: boolean
  noShowFeeMode: 'forfeit_deposit' | 'percentage_of_total' | 'fixed_amount'
  noShowFeePercentage: number | null
  noShowFeeFixedCents: number | null
  whatsappNotificationsEnabled: boolean
  pixKey: string | null
  pixKeyType: PixKeyType | null
  pixMerchantName: string | null
  pixMerchantCity: string | null
}

export type PixKeyType = 'cpf' | 'cnpj' | 'email' | 'phone' | 'evp'

export interface UpdateTenantSettingsInput {
  name?: string
  timeZone?: string
  depositPercentage?: number
  softHoldDurationHours?: number
  cancellationWindowDays?: number
  rescheduleFeePercentage?: number
  defaultCancellationPolicyId?: string | null
  logoImageUrl?: string | null
  logoImagePublicId?: string | null
  noShowFeeEnabled?: boolean
  noShowFeeMode?: 'forfeit_deposit' | 'percentage_of_total' | 'fixed_amount'
  noShowFeePercentage?: number | null
  noShowFeeFixedCents?: number | null
  whatsappNotificationsEnabled?: boolean
  pixKey?: string | null
  pixKeyType?: PixKeyType | null
  pixMerchantName?: string | null
  pixMerchantCity?: string | null
}

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:4000'

type ApiErrorPayload = {
  message?: string | string[]
  details?: string
  errors?: Array<{ field?: string; message?: string }>
  fieldErrors?: Record<string, string[]>
  statusCode?: number
}

export class ApiRequestError extends Error {
  statusCode: number
  fieldErrors: Record<string, string[]>

  constructor(message: string, statusCode: number, fieldErrors: Record<string, string[]> = {}) {
    super(message)
    this.name = 'ApiRequestError'
    this.statusCode = statusCode
    this.fieldErrors = fieldErrors
  }
}

function getCsrfToken(): string | null {
  if (typeof document === 'undefined') {
    return null
  }

  const csrfPair = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith('csrf_token='))

  if (!csrfPair) {
    return null
  }

  return decodeURIComponent(csrfPair.substring('csrf_token='.length))
}

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback
}

async function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const method = (options.method ?? 'GET').toUpperCase()
  const csrfToken = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)
    ? getCsrfToken()
    : null
  
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(csrfToken ? { 'x-csrf-token': csrfToken } : {}),
      ...options.headers,
    },
    credentials: 'include',
  })

  // If unauthorized, surface it to the caller (AuthContext/ProtectedRoute should handle session state)
  if (!response.ok && response.status === 401) {
    throw new Error('Unauthorized')
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' } as ApiErrorPayload)) as ApiErrorPayload
    const normalizedMessage = Array.isArray(error.message)
      ? error.message.join(', ')
      : error.message
    const fallbackFieldErrors = (error.errors || []).reduce<Record<string, string[]>>((accumulator, fieldError) => {
      if (!fieldError?.field || !fieldError?.message) {
        return accumulator
      }

      if (!accumulator[fieldError.field]) {
        accumulator[fieldError.field] = []
      }

      accumulator[fieldError.field].push(fieldError.message)
      return accumulator
    }, {})

    const mergedFieldErrors = {
      ...fallbackFieldErrors,
      ...(error.fieldErrors || {}),
    }

    const firstFieldError = Object.values(mergedFieldErrors)[0]?.[0]
      || error.errors?.find((fieldError) => fieldError?.message)?.message
    const resolvedMessage = firstFieldError || error.details || normalizedMessage || `HTTP ${response.status}`

    throw new ApiRequestError(resolvedMessage, response.status, mergedFieldErrors)
  }

  if (response.status === 204) {
    return undefined as T
  }

  const responseText = await response.text()
  if (!responseText.trim()) {
    return undefined as T
  }

  return JSON.parse(responseText) as T
}

export interface CreateProposalInput {
  clientName: string
  clientEmail: string
  clientPhone?: string
  eventDate?: string
  location?: string
  packageId?: string
  billingMode?: BillingMode
  depositPct: number
  language?: 'en' | 'pt-BR'
  expiresAt?: string
  notes?: string
  allowDateSelection?: boolean
  allowPackageSelection?: boolean
  allowProfessionalSelection?: boolean
  assignedProfessionalId?: string | null
  packageDisplayMode?: 'default' | 'pricing_plan'
  featuredPackageId?: string
  featuredPackageLabel?: string
  sendImmediately?: boolean
  sendWhatsApp?: boolean
}

export type CreateProposalResult =
  | {
      kind: 'proposal_created'
      proposal: Proposal
      publicShareUrl: string
    }
  | {
      kind: 'waitlist_created'
      waitlistEntryId: string
      dateKey: string
      position: number
      conflictingProposalId: string
      message: string
    }

export interface CreateWaitlistEntryInput {
  clientName: string
  clientEmail: string
  clientPhone: string
  preferredLanguage: 'en' | 'es' | 'pt-BR'
}

export function useProposalsApi() {
  const [proposals, setProposals] = useState<Proposal[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchProposals = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await apiRequest<Proposal[]>('/api/proposals')
      setProposals(data)
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to fetch proposals'))
      console.error('Failed to fetch proposals:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  const createProposal = useCallback(async (input: CreateProposalInput): Promise<CreateProposalResult> => {
    const data = await apiRequest<CreateProposalResult>('/api/proposals', {
      method: 'POST',
      body: JSON.stringify(input),
    })
    
    if (data.kind === 'proposal_created') {
      await fetchProposals()
    }

    return data
  }, [fetchProposals])

  const cancelProposal = useCallback(async (proposalId: string, options?: CancelProposalOptions): Promise<CancelProposalResult> => {
    const result = await apiRequest<CancelProposalResult>(`/api/proposals/${proposalId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason: options?.reason, forceMajeure: options?.forceMajeure }),
    })

    // Refresh list after cancellation
    await fetchProposals()
    return result
  }, [fetchProposals])

  useEffect(() => {
    fetchProposals()
  }, [fetchProposals])

  return {
    proposals,
    loading,
    error,
    createProposal,
    cancelProposal,
    refetch: fetchProposals,
  }
}

// Hook to fetch single proposal details
export function useProposalDetails(proposalId: string | null) {
  const [proposal, setProposal] = useState<ProposalDetails | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchProposal = useCallback(async () => {
    if (!proposalId) {
      setProposal(null)
      return
    }

    try {
      setLoading(true)
      setError(null)
      const data = await apiRequest<ProposalDetails>(`/api/proposals/${proposalId}`)
      setProposal(data)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to fetch proposal'
      setError(message)
      console.error('Failed to fetch proposal details:', err)
    } finally {
      setLoading(false)
    }
  }, [proposalId])

  useEffect(() => {
    fetchProposal()
  }, [fetchProposal])

  return {
    proposal,
    loading,
    error,
    refetch: fetchProposal,
  }
}

export function useProposalWaitlist(proposalId: string | null) {
  const [data, setData] = useState<ProposalWaitlistResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<ApiRequestError | Error | null>(null)

  const fetchWaitlist = useCallback(async () => {
    if (!proposalId) {
      setData(null)
      return
    }

    try {
      setLoading(true)
      setError(null)
      const response = await apiRequest<ProposalWaitlistResponse>(`/api/proposals/${proposalId}/waitlist`)
      setData(response)
    } catch (err: unknown) {
      setData(null)
      setError(err instanceof Error ? err : new Error('Failed to fetch waitlist'))
    } finally {
      setLoading(false)
    }
  }, [proposalId])

  useEffect(() => {
    fetchWaitlist()
  }, [fetchWaitlist])

  return {
    data,
    loading,
    error,
    refetch: fetchWaitlist,
  }
}

export function useProposalWaitlistActions(proposalId: string | null) {
  const createEntry = useCallback(async (input: CreateWaitlistEntryInput) => {
    if (!proposalId) throw new Error('Proposal is required')
    return apiRequest<{ status: WaitlistStatus; dateKey: string; position: number; message: string }>(
      `/api/proposals/${proposalId}/waitlist`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
    )
  }, [proposalId])

  const skipEntry = useCallback(async (entryId: string) => {
    if (!proposalId) throw new Error('Proposal is required')
    return apiRequest(`/api/proposals/${proposalId}/waitlist/${entryId}/skip`, {
      method: 'POST',
    })
  }, [proposalId])

  const resendOffer = useCallback(async (entryId: string) => {
    if (!proposalId) throw new Error('Proposal is required')
    return apiRequest(`/api/proposals/${proposalId}/waitlist/${entryId}/resend`, {
      method: 'POST',
    })
  }, [proposalId])

  const removeEntry = useCallback(async (entryId: string) => {
    if (!proposalId) throw new Error('Proposal is required')
    return apiRequest(`/api/proposals/${proposalId}/waitlist/${entryId}`, {
      method: 'DELETE',
    })
  }, [proposalId])

  return {
    createEntry,
    skipEntry,
    resendOffer,
    removeEntry,
  }
}

export interface CancelProposalOptions {
  reason?: string
  forceMajeure?: boolean
}

export interface CancelProposalResult {
  status: string
  refund: {
    amountCents: number
    retainedCents: number
    percentage: number
    policyApplied: string | null
  }
}

export interface CancellationOptions {
  canCancel: boolean
  requiresForceMajeure: boolean
  refundPossible: boolean
  canReschedule: boolean
  paidTotalCents: number
  refundableTotalCents: number
  currency: string | null
  blockedReason: 'already_cancelled' | 'expired' | 'refund_not_possible' | null
}

export function useProposalActions() {
  const cancelProposal = useCallback(async (proposalId: string, options?: CancelProposalOptions): Promise<CancelProposalResult> => {
    return apiRequest<CancelProposalResult>(`/api/proposals/${proposalId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason: options?.reason, forceMajeure: options?.forceMajeure }),
    })
  }, [])

  const getCancellationOptions = useCallback(async (proposalId: string): Promise<CancellationOptions> => {
    return apiRequest<CancellationOptions>(`/api/proposals/${proposalId}/cancellation-options`)
  }, [])

  const markNoShow = useCallback(async (proposalId: string, applyFee?: boolean): Promise<void> => {
    await apiRequest(`/api/proposals/${proposalId}/mark-no-show`, {
      method: 'POST',
      body: JSON.stringify({ applyFee }),
    })
  }, [])

  const getNoShowFeePreview = useCallback(async (proposalId: string) => {
    return apiRequest<{ eligible: boolean; reason?: string; feeCents: number; currency?: string; mode: string }>(`/api/proposals/${proposalId}/no-show-fee-preview`)
  }, [])

  const applyNoShowFee = useCallback(async (proposalId: string): Promise<void> => {
    await apiRequest(`/api/proposals/${proposalId}/no-show-fee/apply`, { method: 'POST' })
  }, [])

  const waiveNoShowFee = useCallback(async (proposalId: string): Promise<void> => {
    await apiRequest(`/api/proposals/${proposalId}/no-show-fee/waive`, { method: 'POST' })
  }, [])

  return {
    cancelProposal,
    getCancellationOptions,
    markNoShow,
    getNoShowFeePreview,
    applyNoShowFee,
    waiveNoShowFee,
  }
}

// Helper to map API response (camelCase) to frontend type (snake_case)
function mapPackageFromApi(pkg: Record<string, unknown>): Package {
  return {
    id: pkg.id as string,
    title: pkg.title as string,
    description: (pkg.description as string | null) ?? null,
    price_cents: (pkg.priceCents as number) ?? (pkg.price_cents as number) ?? 0,
    currency: pkg.currency as string,
    billing_mode: (pkg.billingMode as BillingMode) ?? (pkg.billing_mode as BillingMode) ?? 'one_off',
    recurring_interval: (pkg.recurringInterval as Package['recurring_interval']) ?? (pkg.recurring_interval as Package['recurring_interval']) ?? null,
    recurring_interval_count: (pkg.recurringIntervalCount as number | null) ?? (pkg.recurring_interval_count as number | null) ?? null,
    recurring_plan_id: (pkg.recurringPlanId as string | null) ?? (pkg.recurring_plan_id as string | null) ?? null,
    stripe_product_id: (pkg.stripeProductId as string | null) ?? (pkg.stripe_product_id as string | null) ?? null,
    stripe_price_id: (pkg.stripePriceId as string | null) ?? (pkg.stripe_price_id as string | null) ?? null,
    stripe_sync_status: (pkg.stripeSyncStatus as Package['stripe_sync_status']) ?? (pkg.stripe_sync_status as Package['stripe_sync_status']) ?? 'pending',
    stripe_sync_error: (pkg.stripeSyncError as string | null) ?? (pkg.stripe_sync_error as string | null) ?? null,
    deleted_at: (pkg.deletedAt as string | null) ?? (pkg.deleted_at as string | null) ?? null,
    active: pkg.active as boolean,
    created_at: (pkg.createdAt as string) ?? (pkg.created_at as string) ?? new Date().toISOString(),
    updated_at: (pkg.updatedAt as string) ?? (pkg.updated_at as string) ?? undefined,
    booking_lock_mode: (pkg.bookingLockMode as BookingLockMode) ?? (pkg.booking_lock_mode as BookingLockMode) ?? 'all_day',
    duration_minutes: (pkg.durationMinutes as number | null) ?? (pkg.duration_minutes as number | null) ?? null,
    service_cadence_unit: (pkg.serviceCadenceUnit as ServiceCadenceUnit | null) ?? (pkg.service_cadence_unit as ServiceCadenceUnit | null) ?? null,
    service_cadence_interval_count: (pkg.serviceCadenceIntervalCount as number | null) ?? (pkg.service_cadence_interval_count as number | null) ?? null,
    sessions_per_cadence: (pkg.sessionsPerCadence as number | null) ?? (pkg.sessions_per_cadence as number | null) ?? null,
    service_cadence_required: (pkg.serviceCadenceRequired as boolean) ?? (pkg.service_cadence_required as boolean) ?? false,
    image_url: (pkg.imageUrl as string | null) ?? (pkg.image_url as string | null) ?? null,
    image_public_id: (pkg.imagePublicId as string | null) ?? (pkg.image_public_id as string | null) ?? null,
    benefits: (pkg.benefits as PackageBenefit[] | undefined) ?? [],
    show_on_public_page: (pkg.showOnPublicPage as boolean | undefined) ?? (pkg.show_on_public_page as boolean | undefined) ?? false,
  }
}

export function usePackagesApi() {
  const [packages, setPackages] = useState<Package[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchPackages = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await apiRequest<Record<string, unknown>[]>('/api/packages')
      setPackages(data.map(mapPackageFromApi))
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to fetch packages'))
      console.error('Failed to fetch packages:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  const createPackage = useCallback(async (input: {
    title: string
    description?: string
    priceCents: number
    currency: string
    billingMode?: BillingMode
    bookingLockMode?: BookingLockMode
    durationMinutes?: number
    recurringInterval?: Package['recurring_interval']
    recurringIntervalCount?: number
    recurringPlanId?: string | null
    serviceSchedule?: ServiceScheduleInput
    imageUrl?: string
    imagePublicId?: string
    benefits?: PackageBenefit[]
  }): Promise<Package> => {
    const data = await apiRequest<Package>('/api/packages', {
      method: 'POST',
      body: JSON.stringify(input),
    })
    
    await fetchPackages()
    return data
  }, [fetchPackages])

  const updatePackage = useCallback(async (packageId: string, input: {
    title?: string
    description?: string
    priceCents?: number
    currency?: string
    billingMode?: BillingMode
    bookingLockMode?: BookingLockMode
    durationMinutes?: number
    recurringInterval?: Package['recurring_interval']
    recurringIntervalCount?: number
    recurringPlanId?: string | null
    serviceSchedule?: ServiceScheduleInput
    active?: boolean
    imageUrl?: string
    imagePublicId?: string
    benefits?: PackageBenefit[]
    showOnPublicPage?: boolean
  }): Promise<Package> => {
    const data = await apiRequest<Package>(`/api/packages/${packageId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    })
    
    await fetchPackages()
    return data
  }, [fetchPackages])

  const deletePackage = useCallback(async (packageId: string): Promise<void> => {
    await apiRequest(`/api/packages/${packageId}`, {
      method: 'DELETE',
    })
    
    await fetchPackages()
  }, [fetchPackages])

  useEffect(() => {
    fetchPackages()
  }, [fetchPackages])

  return {
    packages,
    loading,
    error,
    createPackage,
    updatePackage,
    deletePackage,
    refetch: fetchPackages,
  }
}

interface ServiceCatalogFilters {
  search?: string
  billingMode?: BillingMode | 'all'
  stripeSyncStatus?: Package['stripe_sync_status'] | 'all'
  limit?: number
  cursor?: string | null
}

export function useServicesApi(filters: ServiceCatalogFilters = {}) {
  const [services, setServices] = useState<Package[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [nextCursor, setNextCursor] = useState<string | null>(null)

  const fetchServices = useCallback(async (overrideFilters?: ServiceCatalogFilters) => {
    const merged = { ...filters, ...overrideFilters }

    try {
      setLoading(true)
      setError(null)

      const rawPackages = await apiRequest<Record<string, unknown>[]>('/api/packages')
      const mappedPackages = rawPackages.map(mapPackageFromApi)

      const filteredPackages = mappedPackages.filter((pkg) => {
        if (merged.search?.trim()) {
          const term = merged.search.trim().toLowerCase()
          const title = pkg.title.toLowerCase()
          const description = (pkg.description || '').toLowerCase()
          if (!title.includes(term) && !description.includes(term)) {
            return false
          }
        }

        if (merged.billingMode && merged.billingMode !== 'all' && pkg.billing_mode !== merged.billingMode) {
          return false
        }

        if (merged.stripeSyncStatus && merged.stripeSyncStatus !== 'all') {
          const syncStatus = pkg.stripe_sync_status ?? 'pending'
          if (syncStatus !== merged.stripeSyncStatus) {
            return false
          }
        }

        return true
      })

      const startIndex = merged.cursor
        ? Math.max(filteredPackages.findIndex((pkg) => pkg.id === merged.cursor) + 1, 0)
        : 0
      const pagedFromCursor = filteredPackages.slice(startIndex)
      const limit = typeof merged.limit === 'number' ? merged.limit : undefined
      const hasMore = typeof limit === 'number' ? pagedFromCursor.length > limit : false
      const finalItems = typeof limit === 'number' ? pagedFromCursor.slice(0, limit) : pagedFromCursor
      const computedNextCursor = hasMore ? finalItems[finalItems.length - 1]?.id ?? null : null

      setServices(finalItems)
      setNextCursor(computedNextCursor)
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to fetch services'))
      console.error('Failed to fetch services:', err)
    } finally {
      setLoading(false)
    }
  }, [filters])

  const createService = useCallback(async (input: {
    title: string
    description?: string
    priceCents: number
    currency: string
    billingMode?: BillingMode
    bookingLockMode?: BookingLockMode
    durationMinutes?: number
    recurringInterval?: Package['recurring_interval']
    recurringIntervalCount?: number
    recurringPlanId?: string | null
    serviceSchedule?: ServiceScheduleInput
    imageUrl?: string
    imagePublicId?: string
    benefits?: PackageBenefit[]
  }): Promise<Package> => {
    const data = await apiRequest<Record<string, unknown>>('/api/packages', {
      method: 'POST',
      body: JSON.stringify(input),
    })

    await fetchServices()
    return mapPackageFromApi(data)
  }, [fetchServices])

  const updateService = useCallback(async (serviceId: string, input: {
    title?: string
    description?: string
    priceCents?: number
    currency?: string
    billingMode?: BillingMode
    bookingLockMode?: BookingLockMode
    durationMinutes?: number
    recurringInterval?: Package['recurring_interval']
    recurringIntervalCount?: number
    recurringPlanId?: string | null
    serviceSchedule?: ServiceScheduleInput
    active?: boolean
    imageUrl?: string
    imagePublicId?: string
    benefits?: PackageBenefit[]
  }): Promise<Package> => {
    const data = await apiRequest<Record<string, unknown>>(`/api/packages/${serviceId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    })

    await fetchServices()
    return mapPackageFromApi(data)
  }, [fetchServices])

  const deleteService = useCallback(async (serviceId: string): Promise<void> => {
    await apiRequest(`/api/packages/${serviceId}`, {
      method: 'DELETE',
    })

    await fetchServices()
  }, [fetchServices])

  const resyncService = useCallback(async (serviceId: string): Promise<Package> => {
    let data: Record<string, unknown>
    try {
      data = await apiRequest<Record<string, unknown>>(`/api/services/${serviceId}/resync`, {
        method: 'POST',
      })
    } catch (error) {
      if (error instanceof ApiRequestError && error.statusCode === 404) {
        throw new ApiRequestError('Service sync endpoint is unavailable in this backend version.', 404)
      }
      throw error
    }

    await fetchServices()
    return mapPackageFromApi(data)
  }, [fetchServices])

  useEffect(() => {
    fetchServices()
  }, [fetchServices])

  return {
    services,
    loading,
    error,
    nextCursor,
    fetchServices,
    createService,
    updateService,
    deleteService,
    resyncService,
  }
}

// ===== Contract Templates API =====

export interface CreateContractTemplateInput {
  name: string
  content: string
  version?: string
  isDefault?: boolean
}

export interface UpdateContractTemplateInput {
  name?: string
  content?: string
  version?: string
}

export function useContractTemplatesApi() {
  const [templates, setTemplates] = useState<ContractTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await apiRequest<ContractTemplate[]>('/api/contract-templates')
      setTemplates(data)
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to fetch contract templates'))
      console.error('Failed to fetch contract templates:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  const createTemplate = useCallback(async (input: CreateContractTemplateInput): Promise<ContractTemplate> => {
    const data = await apiRequest<ContractTemplate>('/api/contract-templates', {
      method: 'POST',
      body: JSON.stringify(input),
    })
    
    await fetchTemplates()
    return data
  }, [fetchTemplates])

  const updateTemplate = useCallback(async (
    templateId: string,
    input: UpdateContractTemplateInput
  ): Promise<ContractTemplate> => {
    const data = await apiRequest<ContractTemplate>(`/api/contract-templates/${templateId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    })
    
    await fetchTemplates()
    return data
  }, [fetchTemplates])

  const deleteTemplate = useCallback(async (templateId: string): Promise<void> => {
    await apiRequest(`/api/contract-templates/${templateId}`, {
      method: 'DELETE',
    })
    
    await fetchTemplates()
  }, [fetchTemplates])

  const setDefaultTemplate = useCallback(async (templateId: string): Promise<ContractTemplate> => {
    const data = await apiRequest<ContractTemplate>(`/api/contract-templates/${templateId}/set-default`, {
      method: 'POST',
    })
    
    await fetchTemplates()
    return data
  }, [fetchTemplates])

  useEffect(() => {
    fetchTemplates()
  }, [fetchTemplates])

  return {
    templates,
    loading,
    error,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    setDefaultTemplate,
    refetch: fetchTemplates,
  }
}

// ========================
// Cancellation Policies API
// ========================

export interface CreateCancellationPolicyInput {
  name: string
  description?: string
  isDefault?: boolean
  rules?: Array<{
    daysBeforeEvent: number
    refundPercentage: number
    retentionPercentage: number
    description?: string
    order: number
  }>
}

export interface UpdateCancellationPolicyInput {
  name?: string
  description?: string
  isDefault?: boolean
}

export interface CreateRuleInput {
  daysBeforeEvent: number
  refundPercentage: number
  retentionPercentage: number
  description?: string
  order: number
}

export interface UpdateRuleInput {
  daysBeforeEvent?: number
  refundPercentage?: number
  retentionPercentage?: number
  description?: string
  order?: number
}

export function useCancellationPoliciesApi() {
  const [policies, setPolicies] = useState<CancellationPolicy[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchPolicies = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await apiRequest<CancellationPolicy[]>('/api/cancellation-policies')
      setPolicies(data)
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to fetch cancellation policies'))
      console.error('Failed to fetch cancellation policies:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  const createPolicy = useCallback(async (input: CreateCancellationPolicyInput): Promise<CancellationPolicy> => {
    const data = await apiRequest<CancellationPolicy>('/api/cancellation-policies', {
      method: 'POST',
      body: JSON.stringify(input),
    })
    await fetchPolicies()
    return data
  }, [fetchPolicies])

  const updatePolicy = useCallback(async (
    policyId: string,
    input: UpdateCancellationPolicyInput
  ): Promise<CancellationPolicy> => {
    const data = await apiRequest<CancellationPolicy>(`/api/cancellation-policies/${policyId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    })
    await fetchPolicies()
    return data
  }, [fetchPolicies])

  const deletePolicy = useCallback(async (policyId: string): Promise<void> => {
    await apiRequest(`/api/cancellation-policies/${policyId}`, {
      method: 'DELETE',
    })
    await fetchPolicies()
  }, [fetchPolicies])

  const setDefaultPolicy = useCallback(async (policyId: string): Promise<CancellationPolicy> => {
    const data = await apiRequest<CancellationPolicy>(`/api/cancellation-policies/${policyId}/set-default`, {
      method: 'POST',
    })
    await fetchPolicies()
    return data
  }, [fetchPolicies])

  // Rule management
  const addRule = useCallback(async (policyId: string, rule: CreateRuleInput): Promise<CancellationPolicy> => {
    const data = await apiRequest<CancellationPolicy>(`/api/cancellation-policies/${policyId}/rules`, {
      method: 'POST',
      body: JSON.stringify(rule),
    })
    await fetchPolicies()
    return data
  }, [fetchPolicies])

  const updateRule = useCallback(async (
    policyId: string,
    ruleId: string,
    rule: UpdateRuleInput
  ): Promise<CancellationPolicy> => {
    const data = await apiRequest<CancellationPolicy>(`/api/cancellation-policies/${policyId}/rules/${ruleId}`, {
      method: 'PATCH',
      body: JSON.stringify(rule),
    })
    await fetchPolicies()
    return data
  }, [fetchPolicies])

  const deleteRule = useCallback(async (policyId: string, ruleId: string): Promise<void> => {
    await apiRequest(`/api/cancellation-policies/${policyId}/rules/${ruleId}`, {
      method: 'DELETE',
    })
    await fetchPolicies()
  }, [fetchPolicies])

  useEffect(() => {
    fetchPolicies()
  }, [fetchPolicies])

  return {
    policies,
    loading,
    error,
    createPolicy,
    updatePolicy,
    deletePolicy,
    setDefaultPolicy,
    addRule,
    updateRule,
    deleteRule,
    refetch: fetchPolicies,
  }
}

// ========================
// Tenant Settings API
// ========================

export function useTenantSettingsApi() {
  const [settings, setSettings] = useState<TenantSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await apiRequest<TenantSettings>('/api/tenants/settings')
      setSettings(data)
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to fetch tenant settings'))
      console.error('Failed to fetch tenant settings:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  const updateSettings = useCallback(async (input: UpdateTenantSettingsInput): Promise<TenantSettings> => {
    const data = await apiRequest<TenantSettings>('/api/tenants/settings', {
      method: 'PATCH',
      body: JSON.stringify(input),
    })
    setSettings(data)
    return data
  }, [])

  useEffect(() => {
    fetchSettings()
  }, [fetchSettings])

  return {
    settings,
    loading,
    error,
    updateSettings,
    refetch: fetchSettings,
  }
}

// ========================
// WhatsApp Notification Settings API
// ========================

export interface WhatsAppNotificationSettings {
  waProposalCreated: boolean
  waPaymentConfirmed: boolean
  waReminderD7: boolean
  waReminderD1: boolean
}

export function useWhatsAppNotificationSettingsApi() {
  const [settings, setSettings] = useState<WhatsAppNotificationSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await apiRequest<WhatsAppNotificationSettings>('/api/notifications/whatsapp-settings')
      setSettings(data)
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to fetch WhatsApp notification settings'))
      console.error('Failed to fetch WhatsApp notification settings:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  const updateSettings = useCallback(async (input: Partial<WhatsAppNotificationSettings>): Promise<WhatsAppNotificationSettings> => {
    const data = await apiRequest<WhatsAppNotificationSettings>('/api/notifications/whatsapp-settings', {
      method: 'PATCH',
      body: JSON.stringify(input),
    })
    setSettings(data)
    return data
  }, [])

  useEffect(() => {
    fetchSettings()
  }, [fetchSettings])

  return {
    settings,
    loading,
    error,
    updateSettings,
    refetch: fetchSettings,
  }
}

export function useAvailabilityApi() {
  const [windows, setWindows] = useState<AvailabilityWindow[]>([])
  const [exceptions, setExceptions] = useState<AvailabilityException[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchAvailability = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const [windowsResponse, exceptionsResponse] = await Promise.all([
        apiRequest<AvailabilityWindow[]>('/api/availability/windows'),
        apiRequest<AvailabilityException[]>('/api/availability/exceptions'),
      ])
      setWindows(windowsResponse)
      setExceptions(exceptionsResponse)
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to fetch availability settings'))
    } finally {
      setLoading(false)
    }
  }, [])

  const replaceWindows = useCallback(async (nextWindows: AvailabilityWindow[]) => {
    const data = await apiRequest<AvailabilityWindow[]>('/api/availability/windows', {
      method: 'PUT',
      body: JSON.stringify({ windows: nextWindows }),
    })
    setWindows(data)
    return data
  }, [])

  const createException = useCallback(async (
    input: Omit<AvailabilityException, 'id'>,
  ) => {
    const data = await apiRequest<AvailabilityException>('/api/availability/exceptions', {
      method: 'POST',
      body: JSON.stringify(input),
    })
    setExceptions((prev) => [...prev, data].sort((left, right) => left.date.localeCompare(right.date)))
    return data
  }, [])

  const updateException = useCallback(async (
    id: string,
    input: Partial<Omit<AvailabilityException, 'id'>>,
  ) => {
    const data = await apiRequest<AvailabilityException>(`/api/availability/exceptions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    })
    setExceptions((prev) => prev.map((item) => item.id === id ? data : item))
    return data
  }, [])

  const deleteException = useCallback(async (id: string) => {
    await apiRequest(`/api/availability/exceptions/${id}`, {
      method: 'DELETE',
    })
    setExceptions((prev) => prev.filter((item) => item.id !== id))
  }, [])

  useEffect(() => {
    fetchAvailability()
  }, [fetchAvailability])

  return {
    windows,
    exceptions,
    loading,
    error,
    replaceWindows,
    createException,
    updateException,
    deleteException,
    refetch: fetchAvailability,
  }
}

// ========================
// Add-ons API
// ========================

export interface AddOn {
  id: string
  name: string
  description: string | null
  priceCents: number
  currency: string
  active: boolean
  createdAt: string
  updatedAt: string
}

export interface PackageAddOn {
  addOnId: string
  addOn: AddOn
  included: boolean // If true, add-on is included in package price (not a cart bump)
}

export function useAddOnsApi() {
  const [addOns, setAddOns] = useState<AddOn[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchAddOns = useCallback(async (includeInactive = false) => {
    try {
      setLoading(true)
      setError(null)
      const data = await apiRequest<AddOn[]>(`/api/addons?includeInactive=${includeInactive}`)
      setAddOns(data)
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      setError(errorMessage)
      console.error('Failed to fetch add-ons:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  const getPackageAddOns = useCallback(async (packageId: string): Promise<PackageAddOn[]> => {
    const data = await apiRequest<Array<
      | PackageAddOn
      | (AddOn & {
          included: boolean
        })
    >>(`/api/addons/package/${packageId}`)

    return data.flatMap((item) => {
      if ('addOnId' in item && item.addOnId && 'addOn' in item) {
        return [item]
      }

      if ('id' in item && item.id) {
        const { id, included, ...addOn } = item
        return [{
          addOnId: id,
          addOn: {
            id,
            ...addOn,
          },
          included: included ?? false,
        }]
      }

      return []
    })
  }, [])

  const setPackageAddOns = useCallback(async (packageId: string, addOnIds: string[]): Promise<void> => {
    const normalizedIds = Array.from(new Set(addOnIds.filter((addOnId): addOnId is string => typeof addOnId === 'string' && addOnId.length > 0)))
    await apiRequest(`/api/addons/package/${packageId}/set`, {
      method: 'POST',
      body: JSON.stringify({ addOnIds: normalizedIds }),
    })
  }, [])

  useEffect(() => {
    fetchAddOns()
  }, [fetchAddOns])

  return {
    addOns,
    loading,
    error,
    refetch: fetchAddOns,
    getPackageAddOns,
    setPackageAddOns,
  }
}

// ========================
// Admin API (Owner Backoffice)
// ========================

export interface AdminSummary {
  tenants: number
  users: number
  subscriptions: number
  mrrByCurrency: Array<{ currency: string; amountCents: number }>
}

export interface AdminTenant {
  id: string
  name: string
  slug: string
  planId: string
  currency: string
  createdAt: string
  usersCount: number
  proposalsCount: number
}

export interface AdminUser {
  id: string
  name: string
  email: string
  role: string
  locale: string
  createdAt: string
  bannedAt: string | null
  bannedReason: string | null
  shadowBannedAt: string | null
  shadowBannedReason: string | null
  tenant: {
    id: string
    name: string
    slug: string
  }
}

export interface AdminMessage {
  id: string
  userId: string
  subject: string
  content: string
  sentBy: string
  sentAt: string
  readAt: string | null
}

export interface UserModerationStatus {
  id: string
  name: string
  email: string
  isBanned: boolean
  bannedAt: string | null
  bannedReason: string | null
  isShadowBanned: boolean
  shadowBannedAt: string | null
  shadowBannedReason: string | null
  messagesCount: number
}

export interface AdminClient {
  email: string
  name: string | null
  tenantId: string
  tenantName: string
  tenantSlug: string
  proposalsCount: number
  lastActivityAt: string | null
}

export interface AdminModerationItem {
  id: string
  tenantId: string | null
  tenantName: string
  tenantSlug: string
  severity: 'low' | 'medium' | 'high' | 'critical'
  message: string
  createdAt: string
}

export interface AdminHotmartSubscription {
  id: string
  buyerEmail: string
  buyerName: string | null
  hotmartTransactionId: string
  hotmartSubscriptionId: string | null
  hotmartProductId: string
  hotmartPlanId: string | null
  subscriptionPlanId: string
  status: string
  autoLinked: boolean
  reviewNote: string | null
  graceEndsAt: string | null
  lastEventType: string
  lastWebhookAt: string
  tenant: {
    id: string
    name: string
    slug: string
    planId: string
    billingProvider: string
  } | null
  user: {
    id: string
    email: string
    name: string
  } | null
}

export interface AdminListResponse<T> {
  items: T[]
  total: number
}

export function useAdminApi() {
  const getSummary = useCallback(async (): Promise<AdminSummary> => {
    return apiRequest<AdminSummary>('/api/admin/summary')
  }, [])

  const listTenants = useCallback(async (params?: {
    query?: string
    limit?: number
    offset?: number
  }): Promise<AdminListResponse<AdminTenant>> => {
    const queryParams = new URLSearchParams()
    if (params?.query) queryParams.append('query', params.query)
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString())
    if (params?.offset !== undefined) queryParams.append('offset', params.offset.toString())
    const suffix = queryParams.toString() ? `?${queryParams.toString()}` : ''
    return apiRequest<AdminListResponse<AdminTenant>>(`/api/admin/tenants${suffix}`)
  }, [])

  const listUsers = useCallback(async (params?: {
    query?: string
    limit?: number
    offset?: number
  }): Promise<AdminListResponse<AdminUser>> => {
    const queryParams = new URLSearchParams()
    if (params?.query) queryParams.append('query', params.query)
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString())
    if (params?.offset !== undefined) queryParams.append('offset', params.offset.toString())
    const suffix = queryParams.toString() ? `?${queryParams.toString()}` : ''
    return apiRequest<AdminListResponse<AdminUser>>(`/api/admin/users${suffix}`)
  }, [])

  const listClients = useCallback(async (params?: {
    query?: string
    limit?: number
    offset?: number
  }): Promise<AdminListResponse<AdminClient>> => {
    const queryParams = new URLSearchParams()
    if (params?.query) queryParams.append('query', params.query)
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString())
    if (params?.offset !== undefined) queryParams.append('offset', params.offset.toString())
    const suffix = queryParams.toString() ? `?${queryParams.toString()}` : ''
    return apiRequest<AdminListResponse<AdminClient>>(`/api/admin/clients${suffix}`)
  }, [])

  const listModeration = useCallback(async (params?: {
    limit?: number
    offset?: number
  }): Promise<AdminListResponse<AdminModerationItem>> => {
    const queryParams = new URLSearchParams()
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString())
    if (params?.offset !== undefined) queryParams.append('offset', params.offset.toString())
    const suffix = queryParams.toString() ? `?${queryParams.toString()}` : ''
    return apiRequest<AdminListResponse<AdminModerationItem>>(`/api/admin/moderation${suffix}`)
  }, [])

  const listHotmartSubscriptions = useCallback(async (params?: {
    query?: string
    limit?: number
    offset?: number
  }): Promise<AdminListResponse<AdminHotmartSubscription>> => {
    const queryParams = new URLSearchParams()
    if (params?.query) queryParams.append('query', params.query)
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString())
    if (params?.offset !== undefined) queryParams.append('offset', params.offset.toString())
    const suffix = queryParams.toString() ? `?${queryParams.toString()}` : ''
    return apiRequest<AdminListResponse<AdminHotmartSubscription>>(`/api/admin/hotmart-subscriptions${suffix}`)
  }, [])

  const resendHotmartClaim = useCallback(async (subscriptionId: string) => {
    return apiRequest<{ ok: true }>(`/api/admin/hotmart-subscriptions/${subscriptionId}/resend-claim`, {
      method: 'POST',
    })
  }, [])

  const reprocessHotmartWebhook = useCallback(async (subscriptionId: string) => {
    return apiRequest<{ ok: true }>(`/api/admin/hotmart-subscriptions/${subscriptionId}/reprocess-last-webhook`, {
      method: 'POST',
    })
  }, [])

  const reviewHotmartSubscription = useCallback(async (subscriptionId: string, payload: {
    action: 'block' | 'clear'
    targetTenantId?: string
    resolutionNote?: string
  }) => {
    return apiRequest<{ ok: true }>(`/api/admin/hotmart-subscriptions/${subscriptionId}/review`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    })
  }, [])

  return {
    getSummary,
    listTenants,
    listUsers,
    listClients,
    listModeration,
    listHotmartSubscriptions,
    resendHotmartClaim,
    reprocessHotmartWebhook,
    reviewHotmartSubscription,
  }
}

// ========================
// Beta Invite Types
// ========================

export interface BetaInvite {
  id: string
  code: string
  planId: 'starter' | 'pro' | 'studio'
  durationMonths: number
  maxUses: number | null
  usesCount: number
  redemptionsCount: number
  description: string | null
  expiresAt: string | null
  active: boolean
  createdAt: string
}

export interface BetaInviteRedemption {
  id: string
  tenantId: string
  tenantName: string
  tenantSlug: string
  userId: string
  planGranted: string
  expiresAt: string
  redeemedAt: string
}

export interface CreateBetaInviteInput {
  code?: string
  planId?: 'starter' | 'pro' | 'studio'
  durationMonths?: number
  maxUses?: number | null
  description?: string
  expiresAt?: string | null
}

export interface UpdateTenantPlanInput {
  planId: 'free' | 'starter' | 'pro' | 'studio'
  reason?: string
}

export interface DeleteTenantResult {
  success: boolean
  deleted: {
    tenantId: string
    tenantName: string
    tenantSlug: string
    usersCount: number
    proposalsCount: number
    packagesCount: number
  }
}

export function useAdminActionsApi() {
  const updateTenantPlan = useCallback(async (
    tenantId: string, 
    data: UpdateTenantPlanInput
  ): Promise<{ success: boolean; tenant: AdminTenant; previousPlan: string; newPlan: string }> => {
    return apiRequest(`/api/admin/tenants/${tenantId}/plan`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
  }, [])

  const deleteTenant = useCallback(async (
    tenantId: string,
    confirmName: string
  ): Promise<DeleteTenantResult> => {
    return apiRequest(`/api/admin/tenants/${tenantId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ confirmName }),
    })
  }, [])

  const listBetaInvites = useCallback(async (params?: {
    limit?: number
    offset?: number
    active?: boolean
  }): Promise<AdminListResponse<BetaInvite>> => {
    const queryParams = new URLSearchParams()
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString())
    if (params?.offset !== undefined) queryParams.append('offset', params.offset.toString())
    if (params?.active !== undefined) queryParams.append('active', params.active.toString())
    const suffix = queryParams.toString() ? `?${queryParams.toString()}` : ''
    return apiRequest<AdminListResponse<BetaInvite>>(`/api/admin/beta-invites${suffix}`)
  }, [])

  const createBetaInvite = useCallback(async (
    data: CreateBetaInviteInput
  ): Promise<{ success: boolean; invite: BetaInvite }> => {
    return apiRequest('/api/admin/beta-invites', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
  }, [])

  const updateBetaInvite = useCallback(async (
    inviteId: string,
    data: Partial<Pick<BetaInvite, 'active' | 'maxUses' | 'description' | 'expiresAt'>>
  ): Promise<{ success: boolean; invite: BetaInvite }> => {
    return apiRequest(`/api/admin/beta-invites/${inviteId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
  }, [])

  const deleteBetaInvite = useCallback(async (
    inviteId: string
  ): Promise<{ success: boolean; deleted: boolean; deactivated: boolean }> => {
    return apiRequest(`/api/admin/beta-invites/${inviteId}`, {
      method: 'DELETE',
    })
  }, [])

  const listBetaInviteRedemptions = useCallback(async (
    inviteId: string,
    params?: { limit?: number; offset?: number }
  ): Promise<AdminListResponse<BetaInviteRedemption>> => {
    const queryParams = new URLSearchParams()
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString())
    if (params?.offset !== undefined) queryParams.append('offset', params.offset.toString())
    const suffix = queryParams.toString() ? `?${queryParams.toString()}` : ''
    return apiRequest<AdminListResponse<BetaInviteRedemption>>(`/api/admin/beta-invites/${inviteId}/redemptions${suffix}`)
  }, [])

  // User moderation actions
  const banUser = useCallback(async (
    userId: string,
    reason?: string
  ): Promise<{ success: boolean; user: AdminUser }> => {
    return apiRequest(`/api/admin/users/${userId}/ban`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    })
  }, [])

  const unbanUser = useCallback(async (
    userId: string
  ): Promise<{ success: boolean; user: AdminUser }> => {
    return apiRequest(`/api/admin/users/${userId}/unban`, {
      method: 'PATCH',
    })
  }, [])

  const shadowBanUser = useCallback(async (
    userId: string,
    reason?: string
  ): Promise<{ success: boolean; user: AdminUser }> => {
    return apiRequest(`/api/admin/users/${userId}/shadowban`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    })
  }, [])

  const unshadowBanUser = useCallback(async (
    userId: string
  ): Promise<{ success: boolean; user: AdminUser }> => {
    return apiRequest(`/api/admin/users/${userId}/unshadowban`, {
      method: 'PATCH',
    })
  }, [])

  const sendMessageToUser = useCallback(async (
    userId: string,
    subject: string,
    content: string
  ): Promise<{ success: boolean; message: AdminMessage }> => {
    return apiRequest(`/api/admin/users/${userId}/message`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ subject, content }),
    })
  }, [])

  const getUserMessages = useCallback(async (
    userId: string,
    params?: { limit?: number; offset?: number }
  ): Promise<AdminListResponse<AdminMessage>> => {
    const queryParams = new URLSearchParams()
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString())
    if (params?.offset !== undefined) queryParams.append('offset', params.offset.toString())
    const suffix = queryParams.toString() ? `?${queryParams.toString()}` : ''
    return apiRequest<AdminListResponse<AdminMessage>>(`/api/admin/users/${userId}/messages${suffix}`)
  }, [])

  const getUserModerationStatus = useCallback(async (
    userId: string
  ): Promise<UserModerationStatus> => {
    return apiRequest<UserModerationStatus>(`/api/admin/users/${userId}/moderation-status`)
  }, [])

  return {
    updateTenantPlan,
    deleteTenant,
    listBetaInvites,
    createBetaInvite,
    updateBetaInvite,
    deleteBetaInvite,
    listBetaInviteRedemptions,
    // User moderation
    banUser,
    unbanUser,
    shadowBanUser,
    unshadowBanUser,
    sendMessageToUser,
    getUserMessages,
    getUserModerationStatus,
  }
}

// ========================
// Public Invite Validation
// ========================

export interface ValidateInviteResponse {
  valid: boolean
  reason?: string
  invite?: {
    planId: string
    durationMonths: number
    description?: string
  }
}

export function useInviteApi() {
  const validateInvite = useCallback(async (code: string): Promise<ValidateInviteResponse> => {
    return apiRequest<ValidateInviteResponse>(`/api/auth/validate-invite?code=${encodeURIComponent(code)}`)
  }, [])

  return { validateInvite }
}

// ========================
// Public Booking Page
// ========================

export function usePublicBookingPage(slug: string | undefined) {
  const [data, setData] = useState<PublicBookingPageData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!slug) {
      setLoading(false)
      return
    }

    let cancelled = false

    async function fetchPage() {
      try {
        setLoading(true)
        setError(null)
        const result = await apiRequest<PublicBookingPageData>(`/public/pages/${slug}`)
        if (!cancelled) {
          setData(result)
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setData(null)
          setError(getErrorMessage(err, 'Failed to load booking page'))
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    fetchPage()

    return () => {
      cancelled = true
    }
  }, [slug])

  return { data, loading, error }
}

export interface CreateBookingRequestInput {
  clientName: string
  clientEmail: string
  clientPhone?: string
  packageId?: string
  preferredDate?: string
  message?: string
  locale?: string
}

export function useCreateBookingRequest() {
  const submit = useCallback(async (slug: string, input: CreateBookingRequestInput): Promise<{ id: string; status: string }> => {
    return apiRequest<{ id: string; status: string }>(`/public/pages/${slug}/booking-requests`, {
      method: 'POST',
      body: JSON.stringify(input),
    })
  }, [])

  return { submit }
}

export interface AcceptingDatesResult {
  acceptingDates: string[]
  timeZone: string
  calendarConnected: boolean
}

export function useAcceptingDates(params: {
  slug?: string
  professionalId?: string | null
  packageId?: string | null
  from?: string
  to?: string
  enabled?: boolean
}) {
  const { slug, professionalId, packageId, from, to, enabled = true } = params
  const [data, setData] = useState<AcceptingDatesResult | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!enabled || !slug || !professionalId) {
      setData(null)
      setLoading(false)
      return
    }

    let cancelled = false
    const query = new URLSearchParams()
    if (packageId) query.set('packageId', packageId)
    if (from) query.set('from', from)
    if (to) query.set('to', to)

    async function fetchAcceptingDates() {
      try {
        setLoading(true)
        const result = await apiRequest<AcceptingDatesResult>(
          `/public/pages/${slug}/professionals/${professionalId}/accepting-dates?${query.toString()}`,
        )
        if (!cancelled) setData(result)
      } catch {
        if (!cancelled) setData(null)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchAcceptingDates()

    return () => {
      cancelled = true
    }
  }, [enabled, slug, professionalId, packageId, from, to])

  return { data, loading }
}

// ========================
// Instant booking (Stage B, transactional)
// ========================

export interface ProfessionalSlot {
  startTime: string
  endTime: string
}

export interface ProfessionalSlotsResult {
  slotsByDate: Record<string, ProfessionalSlot[]>
  timeZone: string
  calendarConnected: boolean
}

export function useProfessionalSlots(params: {
  slug?: string
  professionalId?: string | null
  packageId?: string | null
  from?: string
  to?: string
  enabled?: boolean
}) {
  const { slug, professionalId, packageId, from, to, enabled = true } = params
  const [data, setData] = useState<ProfessionalSlotsResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [reloadIndex, setReloadIndex] = useState(0)
  const refetch = useCallback(() => setReloadIndex((index) => index + 1), [])

  useEffect(() => {
    if (!enabled || !slug || !professionalId) {
      setData(null)
      setLoading(false)
      return
    }

    let cancelled = false
    const query = new URLSearchParams()
    if (packageId) query.set('packageId', packageId)
    if (from) query.set('from', from)
    if (to) query.set('to', to)

    async function fetchSlots() {
      try {
        setLoading(true)
        const result = await apiRequest<ProfessionalSlotsResult>(
          `/public/pages/${slug}/professionals/${professionalId}/slots?${query.toString()}`,
        )
        if (!cancelled) setData(result)
      } catch {
        if (!cancelled) setData(null)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchSlots()

    return () => {
      cancelled = true
    }
  }, [enabled, slug, professionalId, packageId, from, to, reloadIndex])

  return { data, loading, refetch }
}

export type FirstAvailableResult =
  | {
      available: true
      professionalId: string
      displayName: string
      dateKey: string
      startTime: string
      endTime: string
    }
  | { available: false }

export function useFirstAvailable(params: {
  slug?: string
  packageId?: string | null
  from?: string
  to?: string
  enabled?: boolean
}) {
  const { slug, packageId, from, to, enabled = true } = params
  const [data, setData] = useState<FirstAvailableResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [reloadIndex, setReloadIndex] = useState(0)
  const refetch = useCallback(() => setReloadIndex((index) => index + 1), [])

  useEffect(() => {
    if (!enabled || !slug) {
      setData(null)
      setLoading(false)
      return
    }

    let cancelled = false
    const query = new URLSearchParams()
    if (packageId) query.set('packageId', packageId)
    if (from) query.set('from', from)
    if (to) query.set('to', to)

    async function fetchFirstAvailable() {
      try {
        setLoading(true)
        const result = await apiRequest<FirstAvailableResult>(
          `/public/pages/${slug}/first-available?${query.toString()}`,
        )
        if (!cancelled) setData(result)
      } catch {
        if (!cancelled) setData({ available: false })
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchFirstAvailable()

    return () => {
      cancelled = true
    }
  }, [enabled, slug, packageId, from, to, reloadIndex])

  return { data, loading, refetch }
}

export interface InstantCheckoutInput {
  packageId: string
  assignmentMode: 'customer_choice' | 'first_available'
  professionalId?: string
  date: string
  startTime?: string
  clientName: string
  clientEmail: string
  clientPhone?: string
  locale?: string
  acceptedTerms: boolean
}

export interface InstantCheckoutSuccess {
  publicId: string
  checkoutUrl: string
  professional: { id: string; displayName: string }
  startAt: string
  endAt: string
}

export type InstantCheckoutOutcome =
  | { ok: true; data: InstantCheckoutSuccess }
  | { ok: false; status: number; reason?: string }

export function useInstantCheckout() {
  const submit = useCallback(
    async (slug: string, input: InstantCheckoutInput): Promise<InstantCheckoutOutcome> => {
      const csrfToken = getCsrfToken()
      const response = await fetch(`${API_BASE}/public/pages/${slug}/instant-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(csrfToken ? { 'x-csrf-token': csrfToken } : {}),
        },
        credentials: 'include',
        body: JSON.stringify(input),
      })

      if (response.ok) {
        const data = (await response.json()) as InstantCheckoutSuccess
        return { ok: true, data }
      }

      const body = (await response.json().catch(() => ({}))) as { reason?: string }
      return { ok: false, status: response.status, reason: body?.reason }
    },
    [],
  )

  return { submit }
}

// ========================
// Tenant Public Profile API
// ========================

export interface UpdateTenantPublicProfileInput {
  enabled?: boolean
  headline?: string | null
  bio?: string | null
  coverImageUrl?: string | null
  coverImagePublicId?: string | null
  socialLinks?: Record<string, string> | null
  showPrices?: boolean
  allowProfessionalChoice?: boolean
  showTeamSocialLinks?: boolean
}

export function usePublicProfileApi() {
  const [profile, setProfile] = useState<TenantPublicProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await apiRequest<TenantPublicProfile>('/api/tenants/public-profile')
      setProfile(data)
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to fetch public profile'))
      console.error('Failed to fetch public profile:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  const updateProfile = useCallback(async (input: UpdateTenantPublicProfileInput): Promise<TenantPublicProfile> => {
    const data = await apiRequest<TenantPublicProfile>('/api/tenants/public-profile', {
      method: 'PATCH',
      body: JSON.stringify(input),
    })
    setProfile(data)
    return data
  }, [])

  useEffect(() => {
    fetchProfile()
  }, [fetchProfile])

  return {
    profile,
    loading,
    error,
    updateProfile,
    refetch: fetchProfile,
  }
}

export function usePublicPageCustomizationApi() {
  const [customization, setCustomization] = useState<TenantPublicPageCustomization | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchCustomization = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await apiRequest<TenantPublicPageCustomization>('/api/tenants/public-page-customization')
      setCustomization(data)
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to fetch public page customization'))
      console.error('Failed to fetch public page customization:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  const updateCustomization = useCallback(
    async (input: PublicPageThemeSelection): Promise<TenantPublicPageCustomization> => {
      const data = await apiRequest<TenantPublicPageCustomization>('/api/tenants/public-page-customization', {
        method: 'PATCH',
        body: JSON.stringify(input),
      })
      setCustomization(data)
      return data
    },
    [],
  )

  useEffect(() => {
    fetchCustomization()
  }, [fetchCustomization])

  return {
    customization,
    loading,
    error,
    updateCustomization,
    refetch: fetchCustomization,
  }
}

// ========================
// Booking Requests API
// ========================

export interface ConvertBookingRequestInput {
  depositPct: number
  eventDate?: string
  expiresAt?: string
}

export function useBookingRequestsApi() {
  const [requests, setRequests] = useState<BookingRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await apiRequest<BookingRequest[]>('/api/booking-requests')
      setRequests(data)
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to fetch booking requests'))
      console.error('Failed to fetch booking requests:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  const declineRequest = useCallback(async (id: string): Promise<BookingRequest> => {
    const data = await apiRequest<BookingRequest>(`/api/booking-requests/${id}/decline`, {
      method: 'POST',
    })
    await fetchRequests()
    return data
  }, [fetchRequests])

  const convertRequest = useCallback(async (id: string, input: ConvertBookingRequestInput): Promise<Record<string, unknown>> => {
    const data = await apiRequest<Record<string, unknown>>(`/api/booking-requests/${id}/convert`, {
      method: 'POST',
      body: JSON.stringify(input),
    })
    await fetchRequests()
    return data
  }, [fetchRequests])

  useEffect(() => {
    fetchRequests()
  }, [fetchRequests])

  return {
    requests,
    loading,
    error,
    declineRequest,
    convertRequest,
    refetch: fetchRequests,
  }
}

// ========================
// Integrations API
// ========================

export interface IntegrationConnectionStatus {
  connected: boolean
}

export interface IntegrationStripeStatus {
  connected: boolean
  accountId?: string
  chargesEnabled?: boolean
  payoutsEnabled?: boolean
  detailsSubmitted?: boolean
  email?: string
}

export interface IntegrationMercadoPagoStatus {
  connected: boolean
  userId?: string
  nickname?: string
  email?: string
  liveMode?: boolean
  requiresReconnect?: boolean
}

export function useIntegrationsApi() {
  const getGoogleStatus = useCallback(
    () => apiRequest<IntegrationConnectionStatus>('/api/integrations/google/status'),
    [],
  )
  const getOutlookStatus = useCallback(
    () => apiRequest<IntegrationConnectionStatus>('/api/integrations/outlook/status'),
    [],
  )
  const getStripeStatus = useCallback(
    () => apiRequest<IntegrationStripeStatus>('/api/integrations/stripe/status'),
    [],
  )
  const getPixStatus = useCallback(
    () => apiRequest<Record<string, unknown>>('/api/integrations/stripe/pix/status'),
    [],
  )
  const getMercadoPagoStatus = useCallback(
    () => apiRequest<IntegrationMercadoPagoStatus>('/api/integrations/mercadopago/status'),
    [],
  )

  const getGoogleAuthUrl = useCallback(
    () => apiRequest<{ authUrl: string }>('/api/integrations/google/auth-url'),
    [],
  )
  const getOutlookAuthUrl = useCallback(
    () => apiRequest<{ authUrl: string }>('/api/integrations/outlook/auth-url'),
    [],
  )
  const getStripeAuthUrl = useCallback(
    () => apiRequest<{ authUrl: string }>('/api/integrations/stripe/auth-url'),
    [],
  )
  const getMercadoPagoAuthUrl = useCallback(
    () => apiRequest<{ authUrl: string }>('/api/integrations/mercadopago/auth-url'),
    [],
  )

  const disconnectGoogle = useCallback(
    () => apiRequest<void>('/api/integrations/google/disconnect', { method: 'DELETE' }),
    [],
  )
  const disconnectOutlook = useCallback(
    () => apiRequest<void>('/api/integrations/outlook/disconnect', { method: 'DELETE' }),
    [],
  )
  const disconnectStripe = useCallback(
    () => apiRequest<void>('/api/integrations/stripe/disconnect', { method: 'DELETE' }),
    [],
  )
  const disconnectMercadoPago = useCallback(
    () => apiRequest<void>('/api/integrations/mercadopago/disconnect', { method: 'DELETE' }),
    [],
  )

  const getStripeOnboardingLink = useCallback(
    () => apiRequest<{ url: string }>('/api/integrations/stripe/onboarding-link'),
    [],
  )
  const getStripeDashboardLink = useCallback(
    () => apiRequest<{ url: string }>('/api/integrations/stripe/dashboard-link'),
    [],
  )

  // Uses a raw fetch (rather than apiRequest) because the caller needs the
  // response body even when the request fails, to read Pix-specific reason codes.
  const enableStripePix = useCallback(async (): Promise<{ ok: boolean; data: Record<string, unknown> }> => {
    const csrfToken = getCsrfToken()
    const response = await fetch(`${API_BASE}/api/integrations/stripe/pix/enable`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(csrfToken ? { 'x-csrf-token': csrfToken } : {}),
      },
      credentials: 'include',
    })
    const data = await response.json().catch(() => ({}))
    return { ok: response.ok, data }
  }, [])

  return {
    getGoogleStatus,
    getOutlookStatus,
    getStripeStatus,
    getPixStatus,
    getMercadoPagoStatus,
    getGoogleAuthUrl,
    getOutlookAuthUrl,
    getStripeAuthUrl,
    getMercadoPagoAuthUrl,
    disconnectGoogle,
    disconnectOutlook,
    disconnectStripe,
    disconnectMercadoPago,
    getStripeOnboardingLink,
    getStripeDashboardLink,
    enableStripePix,
  }
}

// =============================================
// STUDIO CALENDAR ROUTING
// =============================================

export type CalendarRoutingMode = 'tenant_calendar' | 'member_calendars'
export type CalendarMigrationStatus = 'idle' | 'migrating' | 'partial_failure' | 'completed'

export interface CalendarRoutingBlockerMember {
  userId: string
  name: string
  email: string
}

export interface CalendarRoutingSettingsResponse {
  available: boolean
  canManage: boolean
  modes?: CalendarRoutingMode[]
  settings?: {
    configured: boolean
    activeMode: CalendarRoutingMode
    targetMode: CalendarRoutingMode | null
    migrationStatus: CalendarMigrationStatus
    initiatedByUserId: string | null
    lastChangedAt: string | null
    bookingsTotal: number
    bookingsMigrated: number
    bookingsSkipped: number
    bookingsFailed: number
    lastError: string | null
  }
  prerequisites?: {
    tenantCalendarConnected: boolean
    membersWithoutCalendar: CalendarRoutingBlockerMember[]
  }
}

export interface CalendarRoutingPreflightResponse {
  targetMode: CalendarRoutingMode
  affectedBookings: number
  canProceed: boolean
  blockers: {
    tenantCalendarMissing: boolean
    membersWithoutCalendar: CalendarRoutingBlockerMember[]
    bookingsWithoutProfessional: number
  }
}

export function useCalendarRoutingApi() {
  const getSettings = useCallback(
    () => apiRequest<CalendarRoutingSettingsResponse>('/api/calendar-routing/settings'),
    [],
  )
  const preflight = useCallback(
    (mode: CalendarRoutingMode) =>
      apiRequest<CalendarRoutingPreflightResponse>('/api/calendar-routing/preflight', {
        method: 'POST',
        body: JSON.stringify({ mode }),
      }),
    [],
  )
  const changeMode = useCallback(
    (mode: CalendarRoutingMode) =>
      apiRequest<{ status: string; migrationStatus: CalendarMigrationStatus }>(
        '/api/calendar-routing/mode',
        { method: 'POST', body: JSON.stringify({ mode }) },
      ),
    [],
  )
  const retry = useCallback(
    () => apiRequest<{ status: string }>('/api/calendar-routing/retry', { method: 'POST' }),
    [],
  )

  return { getSettings, preflight, changeMode, retry }
}
